import {
  apiGetDayTrips,
  type DayTrip,
} from '../../../api/dayTrips/dayTripApi'

import {
  apiGetPrivateTours,
  type PrivateTour,
} from '../../../api/privateTours/privateTourApi'

import {
  apiGetSkiTransfers,
  type SkiTransfer,
} from '../../../api/skiTransfers/skiTransferApi'

import { type DashboardApiResponse } from '../config/config'
import type { ServiceRecord } from '../../types'

type ApiResponse<T = unknown> = {
  success: boolean
  message: string
  data?: T
  warning?: string
  errors?: unknown
}

export type ServiceCategory =
  | 'day-trips'
  | 'private-van-tours'
  | 'ski-transfers'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://72.61.92.71:8000'

const badgeForCategory = (
  category: ServiceRecord['category'],
): string => {
  if (category === 'day-trips') return 'Day Trip'
  if (category === 'private-van-tours') return 'Tour'
  return 'Transfer'
}

const normalizeRoute = (
  routeQuery?: string,
  startLocation?: string,
  endLocation?: string,
  subtitle?: string,
) => {
  const sourceRoute = routeQuery ?? startLocation

  if (sourceRoute && endLocation) {
    return `${sourceRoute} ↔ ${endLocation}`
  }

  if (sourceRoute) {
    return String(sourceRoute)
  }

  return String(subtitle ?? 'Service route')
}

const normalizeDescription = (
  description?: string,
  subtitle?: string,
) =>
  String(description ?? subtitle ?? 'No description available')

const parseBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') {
    return value
  }

  if (typeof value === 'number') {
    if (value === 1) return true
    if (value === 0) return false
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()

    if (['true', '1', 'yes'].includes(normalized)) {
      return true
    }

    if (['false', '0', 'no'].includes(normalized)) {
      return false
    }
  }

  return undefined
}

const getActiveValue = (service: any): boolean => {
  const active = parseBoolean(service?.active)

  if (active !== undefined) {
    return active
  }

  const isActive = parseBoolean(service?.is_active)

  if (isActive !== undefined) {
    return isActive
  }

  return true
}

const getHomepageValue = (service: any): boolean => {
  if (service?.showOnHomepage !== undefined) {
    return parseBoolean(service.showOnHomepage) ?? false
  }

  if (service?.show_on_homepage !== undefined) {
    return parseBoolean(service.show_on_homepage) ?? false
  }

  return false
}

const normalizeArray = (value: any): string[] => {
  if (!Array.isArray(value)) {
    return []
  }

  return value
    .map((item: any) => {
      if (typeof item === 'string') {
        return item
      }

      if (!item) {
        return ''
      }

      return item.name ?? item.title ?? item.label ?? item.description ?? ''
    })
    .map((item) => String(item).trim())
    .filter(Boolean)
}

const normalizeMediaUrl = (value: any): string => {
  const image = String(value ?? '').trim()
  if (!image) return ''

  if (
    image.startsWith('http://') ||
    image.startsWith('https://') ||
    image.startsWith('blob:')
  ) {
    return image
  }

  return `${API_BASE_URL}${image.startsWith('/') ? '' : '/'}${image}`
}

const getImages = (service: any): string[] => {
  const images = service?.images ?? service?.gallery_images ?? []

  const normalizedImages = Array.isArray(images)
    ? images
        .map((item: any) => {
          if (typeof item === 'string') return item
          if (!item) return ''
          return item.image ?? item.url ?? item.image_url ?? ''
        })
        .map((item: any) => normalizeMediaUrl(item))
        .filter(Boolean)
    : []

  const primaryImage =
    normalizeMediaUrl(
      service?.coverImage ??
        service?.cover_image ??
        service?.thumbnailImage ??
        service?.thumbnail ??
        service?.image ??
        '',
    )

  if (primaryImage && !normalizedImages.includes(primaryImage)) {
    normalizedImages.unshift(primaryImage)
  }

  return normalizedImages
}

const mapDayTripToService = (trip: DayTrip): ServiceRecord => {
  const raw: any = trip

  return {
    id: `day-trip-${String(raw.id)}`,
    title: raw.title || String(raw.subtitle ?? 'Day Trip'),
    category: 'day-trips',
    badge: badgeForCategory('day-trips'),
    route: normalizeRoute(
      raw.routeQuery ?? raw.route_query,
      raw.startLocation ?? raw.start_location,
      raw.endLocation ?? raw.end_location,
      raw.subtitle,
    ),
    description: normalizeDescription(
      raw.shortDescription ?? raw.short_description ?? raw.description,
      raw.subtitle,
    ),
    itinerary: raw.itinerary ?? '',
    priceEur: Number(raw.price ?? raw.basePrice ?? raw.base_price ?? 0),
    active: getActiveValue(raw),
    showOnHomepage: getHomepageValue(raw),
    startLocation: raw.startLocation ?? raw.start_location ?? '',
    endLocation: raw.endLocation ?? raw.end_location ?? '',
    startLocation2: raw.startLocation2 ?? raw.start_location2 ?? '',
    endLocation2: raw.endLocation2 ?? raw.end_location2 ?? '',
    subtitle: raw.subtitle ?? '',
    people: raw.people ?? raw.maxPeople ?? raw.max_people,
    duration: raw.duration ?? '',
    durationNights: raw.durationNights ?? raw.duration_nights ?? 0,
    distance: raw.distance ?? raw.distance_km ?? '',
    extraPriceEur: Number(raw.extraPrice ?? raw.extra_price ?? 0),
    features: normalizeArray(raw.features),
    excludedFeatures: normalizeArray(raw.excludedFeatures ?? raw.excluded_features),
    goodToKnow: normalizeArray(raw.goodToKnow ?? raw.good_to_know ?? raw.good_to_knows),
    vehicleAssignment: normalizeArray(raw.vehicleAssignment ?? raw.vehicle_assignment) as any,
    images: getImages(raw),
  }
}

const mapPrivateTourToService = (tour: PrivateTour): ServiceRecord => {
  const raw: any = tour

  return {
    id: `private-tour-${String(raw.id)}`,
    title: raw.title || String(raw.subtitle ?? 'Private Tour'),
    category: 'private-van-tours',
    badge: badgeForCategory('private-van-tours'),
    route: normalizeRoute(
      raw.routeQuery ?? raw.route_query,
      raw.startLocation ?? raw.start_location,
      raw.endLocation ?? raw.end_location,
      raw.subtitle,
    ),
    description: normalizeDescription(
      raw.shortDescription ?? raw.short_description ?? raw.description ?? raw.overview,
      raw.subtitle,
    ),
    priceEur: Number(raw.price ?? raw.basePrice ?? raw.base_price ?? 0),
    active: getActiveValue(raw),
    showOnHomepage: getHomepageValue(raw),
    startLocation: raw.startLocation ?? raw.start_location ?? '',
    endLocation: raw.endLocation ?? raw.end_location ?? '',
    startLocation2: raw.startLocation2 ?? raw.start_location2 ?? '',
    endLocation2: raw.endLocation2 ?? raw.end_location2 ?? '',
    subtitle: raw.subtitle ?? '',
    people: raw.people ?? raw.maxPeople ?? raw.max_people,
    duration: raw.duration ?? '',
    durationNights: raw.durationNights ?? raw.duration_nights ?? 0,
    distance: raw.distance ?? raw.distance_km ?? '',
    extraPriceEur: Number(raw.extraPrice ?? raw.extra_price ?? 0),
    features: normalizeArray(raw.features),
    excludedFeatures: normalizeArray(raw.excludedFeatures ?? raw.excluded_features),
    goodToKnow: normalizeArray(raw.goodToKnow ?? raw.good_to_know ?? raw.good_to_knows),
    vehicleAssignment: normalizeArray(raw.vehicleAssignment ?? raw.vehicle_assignment) as any,
    images: getImages(raw),
  }
}

const mapSkiTransferToService = (transfer: SkiTransfer): ServiceRecord => {
  const raw: any = transfer

  return {
    id: `ski-transfer-${String(raw.id)}`,
    title: raw.title || String(raw.subtitle ?? 'Ski Transfer'),
    category: 'ski-transfers',
    sourceNumber:
      raw.sourceNumber ??
      raw.source_number ??
      '',

    sourceName:
      raw.sourceName ??
      raw.source_name ??
      '',
    badge: badgeForCategory('ski-transfers'),
    route: normalizeRoute(
      raw.routeQuery ?? raw.route_query,
      raw.startLocation ?? raw.start_location,
      raw.endLocation ?? raw.end_location,
      raw.subtitle,
    ),
    description: normalizeDescription(
      raw.shortDescription ?? raw.short_description ?? raw.description ?? raw.overview,
      raw.subtitle,
    ),
    priceEur: Number(raw.price ?? raw.basePrice ?? raw.base_price ?? 0),
    active: getActiveValue(raw),
    showOnHomepage: getHomepageValue(raw),
    startLocation: raw.startLocation ?? raw.start_location ?? '',
    endLocation: raw.endLocation ?? raw.end_location ?? '',
    startLocation2: raw.startLocation2 ?? raw.start_location2 ?? '',
    endLocation2: raw.endLocation2 ?? raw.end_location2 ?? '',
    subtitle: raw.subtitle ?? '',
    people: raw.people ?? raw.maxPeople ?? raw.max_people,
    duration: raw.duration ?? raw.time ?? '',
    durationNights: raw.durationNights ?? raw.duration_nights ?? 0,
    distance: raw.distance ?? raw.distance_km ?? '',
    extraPriceEur: Number(raw.extraPrice ?? raw.extra_price ?? 0),
    features: normalizeArray(raw.features),
    excludedFeatures: normalizeArray(raw.excludedFeatures ?? raw.excluded_features),
    goodToKnow: normalizeArray(raw.goodToKnow ?? raw.good_to_know ?? raw.good_to_knows),
    vehicleAssignment: normalizeArray(raw.vehicleAssignment ?? raw.vehicle_assignment) as any,
    images: getImages(raw),
  }
}

const mergeServiceResponses = <T>(
  result: ApiResponse<T[]> | null,
  mapper: (item: T) => ServiceRecord,
): ServiceRecord[] => {
  if (!result?.success || !Array.isArray(result.data)) {
    return []
  }

  return result.data.map(mapper)
}

export async function apiGetServices(): Promise<DashboardApiResponse<ServiceRecord[]>> {
  const requests = await Promise.allSettled([
    apiGetDayTrips(),
    apiGetPrivateTours(),
    apiGetSkiTransfers(),
  ])

  const dayTrips =
    requests[0].status === 'fulfilled'
      ? mergeServiceResponses(requests[0].value as ApiResponse<DayTrip[]>, mapDayTripToService)
      : []

  const privateTours =
    requests[1].status === 'fulfilled'
      ? mergeServiceResponses(requests[1].value as ApiResponse<PrivateTour[]>, mapPrivateTourToService)
      : []

  const skiTransfers =
    requests[2].status === 'fulfilled'
      ? mergeServiceResponses(requests[2].value as ApiResponse<SkiTransfer[]>, mapSkiTransferToService)
      : []

  const warnings: string[] = []

  for (const request of requests) {
    if (request.status === 'fulfilled') {
      if (!request.value.success) {
        warnings.push(request.value.message)
      }
    } else {
      warnings.push(String(request.reason ?? 'Unknown error'))
    }
  }

  const services = [...dayTrips, ...privateTours, ...skiTransfers]
  const success = services.length > 0
  const message = success
    ? 'Services loaded successfully.'
    : warnings.join(' ') || 'Failed to load services.'

  return {
    success,
    message,
    warning: warnings.length ? warnings.join(' ') : undefined,
    data: services,
  }
}

const getBackendId = (id: string): string => {
  const parts = String(id).split('-')
  return parts[parts.length - 1] || id
}

export async function apiGetServiceById(
  id: string,
  category: ServiceCategory,
): Promise<DashboardApiResponse<ServiceRecord>> {
  const backendId = getBackendId(id)

  let endpoint = ''

  if (category === 'day-trips') {
    endpoint = `/api/admin/day-trips/${backendId}/`
  } else if (category === 'private-van-tours') {
    endpoint = `/api/admin/private-tours/${backendId}/`
  } else if (category === 'ski-transfers') {
    endpoint = `/api/admin/ski-transfers/${backendId}/`
  } else {
    return {
      success: false,
      message: 'Invalid service category.',
    }
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    const result = await response.json().catch(() => null)

    if (!response.ok) {
      return {
        success: false,
        message: result?.message || `Failed to fetch ${category} with id ${backendId}.`,
              }
    }

    const raw = result?.data ?? result
    let data: ServiceRecord

    if (category === 'day-trips') {
      data = mapDayTripToService(raw as DayTrip)
    } else if (category === 'private-van-tours') {
      data = mapPrivateTourToService(raw as PrivateTour)
    } else {
      data = mapSkiTransferToService(raw as SkiTransfer)
    }

    return {
      success: true,
      message: result?.message || 'Service details loaded successfully.',
      data,
    }
  } catch (error: any) {
    return {
      success: false,
      message: error?.message || 'Connection failed.',
    }
  }
}


const buildServiceRequestBody = (
  data: Record<string, unknown>,
): { body: BodyInit; headers?: HeadersInit } => {
  const imageFiles = Array.isArray(
    (data as any).imageFiles,
  )
    ? ((data as any).imageFiles as Array<File | null>).filter(
        (file): file is File => file instanceof File,
      )
    : []

  if (imageFiles.length === 0) {
    const cleanData = { ...data }
    delete (cleanData as any).imageFiles

    return {
      body: JSON.stringify(cleanData),
      headers: {
        'Content-Type': 'application/json',
      },
    }
  }

  const multipart = new FormData()

  Object.entries(data).forEach(
    ([key, value]) => {
      if (
        key === 'imageFiles' ||
        value === undefined ||
        value === null
      ) {
        return
      }

      if (Array.isArray(value)) {
        multipart.append(
          key,
          JSON.stringify(value),
        )
      } else {
        multipart.append(
          key,
          String(value),
        )
      }
    },
  )

  const allImageFiles =
    (data as any).imageFiles as Array<File | null>

  allImageFiles.forEach(
    (file, index) => {
      if (!(file instanceof File)) {
        return
      }

      if (index === 0) {
        multipart.append(
          'cover_image',
          file,
        )
        return
      }

      multipart.append(
        'gallery_images',
        file,
      )
    },
  )

  return {
    body: multipart,
  }
}

/**
 * CREATE SERVICE
 *
 * POST:
 * /day-trips/
 * /private-tours/
 * /ski-transfers/
 */
export async function apiCreateService(
  category: ServiceCategory,
  data: Record<
    string,
    unknown
  >,
): Promise<ApiResponse<any>> {
  let endpoint = ''

  if (
    category ===
    'day-trips'
  ) {
    endpoint =
      '/api/admin/day-trips/'
  } else if (
    category ===
    'private-van-tours'
  ) {
    endpoint =
      '/api/admin/private-tours/'
  } else if (
    category ===
    'ski-transfers'
  ) {
    endpoint =
      '/api/admin/ski-transfers/'
  } else {
    return {
      success: false,
      message:
        'Invalid service category.',
    }
  }

  try {
    const requestBody =
      buildServiceRequestBody(data)

    const response =
      await fetch(
        `${API_BASE_URL}${endpoint}`,
        {
          method: 'POST',
          headers:
            requestBody.headers,
          body:
            requestBody.body,
        },
      )

    const result =
      await response
        .json()
        .catch(() => null)

    if (!response.ok) {
      return {
        success: false,
        message:
          result?.message ||
          'Failed to create service.',
        errors:
          result?.errors,
      }
    }

    return {
      success:
        result?.success ??
        true,

      message:
        result?.message ||
        'Service created successfully.',

      data:
        result?.data ??
        result,
    }
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.message ||
        'Connection failed.',
    }
  }
}

/**
 * UPDATE SERVICE
 *
 * PATCH:
 * /day-trips/<id>/
 * /private-tours/<id>/
 * /ski-transfers/<id>/
 */
export async function apiUpdateService(
  category: ServiceCategory,
  id: string,
  data: Record<
    string,
    unknown
  >,
): Promise<ApiResponse<any>> {
  const backendId =
    getBackendId(id)

  let endpoint = ''

  if (
    category ===
    'day-trips'
  ) {
    endpoint =
      `/api/admin/day-trips/${backendId}/`
  } else if (
    category ===
    'private-van-tours'
  ) {
    endpoint =
      `/api/admin/private-tours/${backendId}/`
  } else if (
    category ===
    'ski-transfers'
  ) {
    endpoint =
      `/api/admin/ski-transfers/${backendId}/`
  } else {
    return {
      success: false,
      message:
        'Invalid service category.',
    }
  }

  try {
    const requestBody =
      buildServiceRequestBody(data)

    const response =
      await fetch(
        `${API_BASE_URL}${endpoint}`,
        {
          method: 'PATCH',
          headers:
            requestBody.headers,
          body:
            requestBody.body,
        },
      )

    const result =
      await response
        .json()
        .catch(() => null)

    if (!response.ok) {
      return {
        success: false,
        message:
          result?.message ||
          'Failed to update service.',
        errors:
          result?.errors,
      }
    }

    return {
      success:
        result?.success ??
        true,

      message:
        result?.message ||
        'Service updated successfully.',

      data:
        result?.data ??
        result,
    }
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.message ||
        'Connection failed.',
    }
  }
}

/**
 * UPDATE SERVICE STATUS
 *
 * PATCH:
 * /day-trips/<id>/status/
 * /private-tours/<id>/status/
 * /ski-transfers/<id>/status/
 */
export async function apiToggleServiceStatus(
  category: ServiceCategory,
  id: string,
  active: boolean,
): Promise<ApiResponse<any>> {
  const backendId =
    getBackendId(id)

  let endpoint = ''

  if (
    category ===
    'day-trips'
  ) {
    endpoint =
      `/api/admin/day-trips/${backendId}/status/`
  } else if (
    category ===
    'private-van-tours'
  ) {
    endpoint =
      `/api/admin/private-tours/${backendId}/status/`
  } else if (
    category ===
    'ski-transfers'
  ) {
    endpoint =
      `/api/admin/ski-transfers/${backendId}/status/`
  } else {
    return {
      success: false,
      message:
        'Invalid service category.',
    }
  }

  try {
    const response =
      await fetch(
        `${API_BASE_URL}${endpoint}`,
        {
          method: 'PATCH',

          headers: {
            'Content-Type':
              'application/json',
          },

          body: JSON.stringify({
            active,
          }),
        },
      )

    const result =
      await response
        .json()
        .catch(() => null)

    if (!response.ok) {
      return {
        success: false,
        message:
          result?.message ||
          'Failed to update service status.',
        errors:
          result?.errors,
      }
    }

    return {
      success:
        result?.success ??
        true,

      message:
        result?.message ||
        'Service status updated successfully.',

      data:
        result?.data,
    }
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.message ||
        'Connection failed.',
    }
  }
}

/**
 * FLEET API
 *
 * Uses the existing admin fleet endpoints:
 * GET    /api/admin/fleets/
 * GET    /api/admin/fleets/<id>/
 * POST   /api/admin/fleets/
 * PATCH  /api/admin/fleets/<id>/
 * PATCH  /api/admin/fleets/<id>/status/
 * DELETE /api/admin/fleets/<id>/
 */

export type FleetApiRecord = {
  id: string
  category: string
  name: string
  brand?: string
  plate: string
  image: string
  passengers: number
  luggage: number
  cabinBags?: number
  features?: string[]
  description: string
  basePrice?: number
  pricePerKm?: number
  active: boolean
  showOnHomepage: boolean
}

const getFleetBackendId = (id: string): string => {
  const value = String(id)

  if (value.startsWith('fleet-')) {
    return value.replace('fleet-', '')
  }

  if (value.startsWith('f-')) {
    return value.replace('f-', '')
  }

  return value
}

const normalizeFleetImage = (value: unknown): string => {
  const image = String(value ?? '').trim()

  if (!image) return ''

  if (
    image.startsWith('http://') ||
    image.startsWith('https://') ||
    image.startsWith('blob:') ||
    image.startsWith('data:')
  ) {
    return image
  }

  return `${API_BASE_URL}${image.startsWith('/') ? '' : '/'}${image}`
}

const normalizeFleet = (value: any): FleetApiRecord => {
  const raw = value ?? {}

  return {
    id: `fleet-${String(raw.id ?? '')}`,
    category: String(
      raw.category ??
      raw.vehicle_type ??
      raw.vehicleType ??
      '',
    ),
    name: String(raw.name ?? ''),
    brand:
      raw.brand !== undefined
        ? String(raw.brand)
        : '',
    plate: String(
      raw.plate ??
      raw.registration ??
      raw.license_plate ??
      '',
    ),
    image: normalizeFleetImage(
      raw.image ??
      raw.vehicle_image ??
      '',
    ),
    passengers: Number(
      raw.passengers ??
      raw.max_passengers ??
      0,
    ),
    luggage: Number(
      raw.luggage ??
      raw.suitcases ??
      raw.max_suitcases ??
      0,
    ),
    cabinBags: Number(
      raw.cabinBags ??
      raw.cabin_bags ??
      raw.max_cabin_bags ??
      0,
    ),
    features: Array.isArray(raw.features)
      ? raw.features
      : [],
    description: String(
      raw.description ??
      raw.short_description ??
      '',
    ),
    basePrice: Number(
      raw.basePrice ??
      raw.base_price ??
      0,
    ),
    pricePerKm: Number(
      raw.pricePerKm ??
      raw.price_per_km ??
      0,
    ),
    active:
      raw.active !== undefined
        ? parseBoolean(raw.active) ?? false
        : raw.is_active !== undefined
          ? parseBoolean(raw.is_active) ?? false
          : true,
    showOnHomepage:
      raw.showOnHomepage !== undefined
        ? parseBoolean(raw.showOnHomepage) ?? false
        : raw.show_on_homepage !== undefined
          ? parseBoolean(raw.show_on_homepage) ?? false
          : false,
  }
}

export async function apiGetFleets(): Promise<
  ApiResponse<FleetApiRecord[]>
> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/admin/fleets/`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )

    const result =
      await response.json().catch(() => null)

    if (!response.ok) {
      return {
        success: false,
        message:
          result?.message ||
          'Failed to fetch fleets.',
        errors: result?.errors,
      }
    }

    const rawData =
      Array.isArray(result?.results)
        ? result.results
        : result?.data ?? result

    return {
      success: true,
      message:
        result?.message ||
        'Fleets loaded successfully.',
      data: Array.isArray(rawData)
        ? rawData.map(normalizeFleet)
        : [],
    }
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.message ||
        'Connection failed.',
    }
  }
}

export async function apiGetFleetById(
  id: string,
): Promise<ApiResponse<FleetApiRecord>> {
  const backendId =
    getFleetBackendId(id)

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/admin/fleets/${backendId}/`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )

    const result =
      await response.json().catch(() => null)

    if (!response.ok) {
      return {
        success: false,
        message:
          result?.message ||
          `Failed to fetch fleet with id ${backendId}.`,
        errors: result?.errors,
      }
    }

    return {
      success: true,
      message:
        result?.message ||
        'Fleet details loaded successfully.',
      data: normalizeFleet(
        result?.data ?? result,
      ),
    }
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.message ||
        'Connection failed.',
    }
  }
}

const buildFleetRequestBody = (
  data: Record<string, unknown>,
): { body: BodyInit; headers?: HeadersInit } => {
  const imageFile = (data as any).imageFile

  if (!(imageFile instanceof File)) {
    const cleanData = {
      ...data,
    }

    delete (cleanData as any).imageFile

    return {
      body: JSON.stringify(cleanData),
      headers: {
        'Content-Type':
          'application/json',
      },
    }
  }

  const multipart = new FormData()

  Object.entries(data).forEach(
    ([key, value]) => {
      if (
        key === 'imageFile' ||
        value === undefined ||
        value === null
      ) {
        return
      }

      if (Array.isArray(value)) {
        multipart.append(
          key,
          JSON.stringify(value),
        )
      } else {
        multipart.append(
          key,
          String(value),
        )
      }
    },
  )

  multipart.append(
    'image',
    imageFile,
  )

  return {
    body: multipart,
  }
}

export async function apiCreateFleet(
  data: Record<string, unknown>,
): Promise<ApiResponse<FleetApiRecord>> {
  try {
    const requestBody =
      buildFleetRequestBody(data)

    const response = await fetch(
      `${API_BASE_URL}/api/admin/fleets/`,
      {
        method: 'POST',
        headers:
          requestBody.headers,
        body:
          requestBody.body,
      },
    )

    const result =
      await response.json().catch(() => null)

    if (!response.ok) {
      return {
        success: false,
        message:
          result?.message ||
          'Failed to create fleet.',
        errors: result?.errors,
      }
    }

    return {
      success:
        result?.success ??
        true,
      message:
        result?.message ||
        'Fleet created successfully.',
      data: result?.data
        ? normalizeFleet(result.data)
        : undefined,
    }
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.message ||
        'Connection failed.',
    }
  }
}

export async function apiUpdateFleet(
  id: string,
  data: Record<string, unknown>,
): Promise<ApiResponse<FleetApiRecord>> {
  const backendId =
    getFleetBackendId(id)

  try {
    const requestBody =
      buildFleetRequestBody(data)

    const response = await fetch(
      `${API_BASE_URL}/api/admin/fleets/${backendId}/`,
      {
        method: 'PATCH',
        headers:
          requestBody.headers,
        body:
          requestBody.body,
      },
    )

    const result =
      await response.json().catch(() => null)

    if (!response.ok) {
      return {
        success: false,
        message:
          result?.message ||
          'Failed to update fleet.',
        errors: result?.errors,
      }
    }

    return {
      success:
        result?.success ??
        true,
      message:
        result?.message ||
        'Fleet updated successfully.',
      data: result?.data
        ? normalizeFleet(result.data)
        : undefined,
    }
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.message ||
        'Connection failed.',
    }
  }
}

export async function apiToggleFleetStatus(
  id: string,
  active: boolean,
): Promise<ApiResponse<{
  id: string
  active: boolean
}>> {
  const backendId =
    getFleetBackendId(id)

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/admin/fleets/${backendId}/status/`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type':
            'application/json',
        },
        body: JSON.stringify({
          active,
        }),
      },
    )

    const result =
      await response.json().catch(() => null)

    if (!response.ok) {
      return {
        success: false,
        message:
          result?.message ||
          'Failed to update fleet status.',
        errors: result?.errors,
      }
    }

    return {
      success:
        result?.success ??
        true,
      message:
        result?.message ||
        'Fleet status updated successfully.',
      data: result?.data,
    }
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.message ||
        'Connection failed.',
    }
  }
}

export async function apiDeleteFleet(
  id: string,
  permanent = false,
): Promise<ApiResponse<unknown>> {
  const backendId =
    getFleetBackendId(id)

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/admin/fleets/${backendId}/${permanent ? '?permanent=true' : ''}`,
      {
        method: 'DELETE',
      },
    )

    const result =
      await response.json().catch(() => null)

    if (!response.ok) {
      return {
        success: false,
        message:
          result?.message ||
          'Failed to delete fleet.',
        errors: result?.errors,
      }
    }

    return {
      success:
        result?.success ??
        true,
      message:
        result?.message ||
        'Fleet deleted successfully.',
      data:
        result?.data,
    }
  } catch (error: any) {
    return {
      success: false,
      message:
        error?.message ||
        'Connection failed.',
    }
  }
}
